import React from "react"
import {ArticleContainer, Main} from "../../styles/Container"
import styled from "@emotion/styled"
import Head from "@nvdb-rapport/shared/src/components/Head"
import * as R from "remeda"
import {
  GeneriskAction,
  GeneriskModel,
} from "./update"
import tw from "twin.macro"
import {GeneriskQueryData, requestGeneriskReport} from "../../data/api"
import {UIAction, UIModel} from "../../update/ui"
import {AppState} from "../../types"
import {ReportAction} from "../../update/report"
import {Just} from "purify-ts/Maybe"
import ObjectSelection from "./ObjectSelection";
import ReportTypeRadioGroup from "../FormComponents/ReportTypeRadioGroup";
import FormatSelection from "../FormComponents/FormatSelection";
import SearchCriteria from "../FormComponents/SearchCriteria";
import Submit from "../FormComponents/Submit";
import {ReportType} from "../../models/ReportType";

interface SearchFilterProps {
  generiskState: GeneriskModel;
  generiskDispatch: React.Dispatch<GeneriskAction>;
  uiDispatch: React.Dispatch<UIAction>;
  reportDispatch: React.Dispatch<ReportAction>;
  uiState: UIModel;
}

const formValidationConditions: ((m: GeneriskModel) => boolean)[] = [
  m =>
    m.selectedContractArea.isJust() ||
    m.selectedFylker.value.length > 0 ||
    m.selectedKommuner.value.length > 0,
]

export function GeneriskRapportForm({
  generiskState,
  uiState,
  generiskDispatch,
  uiDispatch,
  reportDispatch,
}: SearchFilterProps) {
  async function getContract(query: GeneriskQueryData) {
    uiDispatch({
      type: "UpdateAppState",
      payload: AppState.FETCHING_FORM_QUERY,
    })

    reportDispatch({ type: "SetQueriedAt", payload: new Date() })

    try {
      const decodedPollData = await requestGeneriskReport(query)

      decodedPollData.caseOf({
        Left: e => {
          uiDispatch({
            type: "AddError",
            payload: {
              message: e,
              title: "Dekoder-feil ved innhenting av rapport",
            },
          })
        },
        Right: pollData => {
          if (pollData.status === "PENDING") {
            uiDispatch({
              type: "UpdateAppState",
              payload: AppState.CONTRACT_PENDING,
            })
            reportDispatch({
              type: "SetReportId",
              payload: Just(pollData.id),
            })
          }
          reportDispatch({
            type: "SetReport",
            payload: Just(pollData),
          })
        },
      })
    } catch (e) {
      console.log("error requestDriftskontraktReport", e)
      uiDispatch({
        type: "AddError",
        payload: { message: e.message, title: e.name },
      })
    }
  }

  const formIsValid = R.allPass(generiskState, formValidationConditions)

  function getRoadObjects(): number[] | undefined {
    const id = generiskState.selectedReportType.extract()?.id;

    if (id === ReportType.DETALJERT_MENGDEOVERSIKT) {
      return R.map(generiskState.customReport.add, o => o.id);
    }

    if (id === ReportType.OVERLAPP) {
      const primary = generiskState.selectedVegobjekt.extract();
      const secondary = generiskState.selectedSecondaryVegobjekt.extract();
      if (primary) {
        return secondary ? [primary.id, secondary.id] : [primary.id];
      }
    }

    if (id === ReportType.RELASJON_EXCEL || id === ReportType.RELASJON_GEOPACKAGE || id === ReportType.OBJECT_HOLE) {
      const primary = generiskState.selectedVegobjekt.extract();
      if (primary) {
        return [primary.id];
      }
    }

    return undefined;
  }

  function handleSubmit(e: React.FormEvent) {
    console.log("Submit")
    e.preventDefault()

    formIsValid &&
      generiskState.selectedReportType.chain(selectedReportType => {
        generiskState.selectedContractArea.caseOf({
          Just: selectedContractArea => {
            const query: GeneriskQueryData = {
              kontraktsomradeId: selectedContractArea.id,
              dato: generiskState.date.extract(),
              rapporttype: selectedReportType.id,
              spraak: "nb_NO",
              vegsystemreferanser: generiskState.vegsystem.caseOf({
                Left: () => [] as string[],
                Right: ref => ref,
              }),
              vegobjekttyper: getRoadObjects()
            }

            getContract(query)
          },
          Nothing: () => {
            const query: GeneriskQueryData = {
              dato: generiskState.date.extract(),
              rapporttype: selectedReportType.id,
              fylke: R.map(generiskState.selectedFylker.value, f => f.nummer),
              kommune: R.map(
                generiskState.selectedKommuner.value,
                k => k.nummer
              ),
              spraak: "nb_NO",
              vegsystemreferanser: generiskState.vegsystem.caseOf({
                Left: () => [] as string[],
                Right: ref => ref,
              }),
              vegobjekttyper: getRoadObjects(),
            }
            getContract(query)
          },
        })
        return Just(selectedReportType)
      })
  }

  return (
    <>
      <Head
        siteTitle="Vegnett og objektdata for driftskontrakter"
        status="default"
      />
      <Main style={tw`flex-col items-center`}>
        <ArticleContainer>
          <h2 style={tw`text-4xl font-bold my-4 text-left mb-12`}>
            Egendefinerte rapporter
          </h2>
          <StyledForm onSubmit={handleSubmit}>
            <h3 style={tw`text-xl font-bold text-left m-0 w-full`}>
              Velg Rapporttype
            </h3>
            <ReportTypeRadioGroup
              generiskState={generiskState}
              generiskDispatch={generiskDispatch}
            />
            <FormatSelection
              generiskState={generiskState}
              generiskDispatch={generiskDispatch}
            />
            <ObjectSelection
              generiskState={generiskState}
              generiskDispatch={generiskDispatch}
              uiDispatch={uiDispatch}
            />
            <SearchCriteria
              generiskState={generiskState}
              generiskDispatch={generiskDispatch}
              formIsValid={formIsValid}
            />
            <Submit
              generiskState={generiskState}
              generiskDispatch={generiskDispatch}
              uiState={uiState}
              formIsValid={formIsValid}
            />
          </StyledForm>
        </ArticleContainer>
      </Main>
    </>
  )
}

const StyledForm = styled.form`
  display: grid;
  flex-direction: column;
  width: 100%;
  grid-gap: 3rem;
  align-items: center;
  text-align: left;
`
